import type {
  AnalyticsActivityBreakdownDto,
  AnalyticsOperationsResponseDto,
  AnalyticsProductsResponseDto,
  AnalyticsSummaryDto,
  AnalyticsWorkersResponseDto,
  CalculationPolicyVersionDto,
  CompanyPerformanceSettingsInput,
  CompanyPerformanceSettingsVersionDto,
  CreateCalculationPolicyVersionInput,
  CreateNeutralActivityInput,
  CreateNormInput,
  CreateOperationInput,
  CreateProductInput,
  CreateUnitInput,
  CreateWorkerInput,
  CreateWorkLineInput,
  DailyEntryHistoryItemDto,
  DailyWorkEntryDto,
  DeletedDailyEntryInfoDto,
  DepartmentDto,
  LatestReportingDayDto,
  MeResponseDto,
  NeutralActivityDto,
  NormDto,
  NormHistoryItemDto,
  NormRecommendationDto,
  OperationDto,
  ProductDto,
  ReportDocument,
  ReportPreviewRequestInput,
  ReportTypeCatalogEntry,
  SupersedeNormInput,
  UnitDto,
  UpdateNeutralActivityInput,
  UpdateOperationInput,
  UpdateProductInput,
  UpdateUnitInput,
  UpdateWorkerInput,
  UpdateWorkLineInput,
  UserDto,
  ValidCombinationDto,
  WorkerDto,
  WorkLineDto,
} from "@normax-performance/shared-types";
import { apiRequest, apiRequestFile, apiRequestFileGet, apiRequestUpload } from "./client";

// Mirrors the server-side shapes in
// apps/api/src/modules/catalog/catalog-import-export.service.ts — not
// exported from @normax-performance/shared-types, so declared locally here
// rather than expanding that already-reviewed server file's scope.
interface ImportRowError {
  row: number;
  message: string;
}
interface ImportRowSkipped {
  row: number;
  reason: "ALREADY_EXISTS";
}
export interface ImportResult {
  created: number;
  skipped: ImportRowSkipped[];
  errors: ImportRowError[];
}

export const authApi = {
  login: (companyCode: string, email: string, password: string) =>
    apiRequest<{ user: UserDto }>("/auth/login", {
      method: "POST",
      body: { companyCode, email, password },
    }),
  logout: () => apiRequest<void>("/auth/logout", { method: "POST" }),
  me: () => apiRequest<MeResponseDto>("/auth/me"),
};

export const workersApi = {
  // `includeInactive` omitted (undefined) -> identical query to the
  // pre-Slice-A call, so every existing no-arg caller (Daily Entry,
  // Dashboard) is unaffected. Only the Workers admin screen passes `true`.
  list: (params?: { includeInactive?: boolean }) =>
    apiRequest<WorkerDto[]>("/workers", {
      query: { includeInactive: params?.includeInactive ? "true" : undefined },
    }),
  create: (input: CreateWorkerInput) =>
    apiRequest<WorkerDto>("/workers", { method: "POST", body: input }),
  update: (id: string, input: UpdateWorkerInput) =>
    apiRequest<WorkerDto>(`/workers/${id}`, { method: "PATCH", body: input }),
};

export const catalogApi = {
  products: (params?: { includeInactive?: boolean }) =>
    apiRequest<ProductDto[]>("/products", {
      query: { includeInactive: params?.includeInactive ? "true" : undefined },
    }),
  createProduct: (input: CreateProductInput) =>
    apiRequest<ProductDto>("/products", { method: "POST", body: input }),
  updateProduct: (id: string, input: UpdateProductInput) =>
    apiRequest<ProductDto>(`/products/${id}`, { method: "PATCH", body: input }),
  exportProducts: (params: { includeInactive?: boolean }) => apiRequestFile("/products/export", params),
  importTemplateProducts: () => apiRequestFileGet("/products/import-template"),
  importProducts: (file: File) => apiRequestUpload<ImportResult>("/products/import", file, "file"),
  operations: (params?: { includeInactive?: boolean }) =>
    apiRequest<OperationDto[]>("/operations", {
      query: { includeInactive: params?.includeInactive ? "true" : undefined },
    }),
  createOperation: (input: CreateOperationInput) =>
    apiRequest<OperationDto>("/operations", { method: "POST", body: input }),
  updateOperation: (id: string, input: UpdateOperationInput) =>
    apiRequest<OperationDto>(`/operations/${id}`, { method: "PATCH", body: input }),
  exportOperations: (params: { includeInactive?: boolean }) => apiRequestFile("/operations/export", params),
  importTemplateOperations: () => apiRequestFileGet("/operations/import-template"),
  importOperations: (file: File) => apiRequestUpload<ImportResult>("/operations/import", file, "file"),
  // Bare no-arg call stays identical to the pre-existing behavior (active-only)
  // — Daily Entry's picker query is completely unaffected. Only the Master
  // Data admin screen passes `{ includeInactive: true }`. Same pattern as
  // `products`/`operations` above.
  neutralActivities: (params?: { includeInactive?: boolean }) =>
    apiRequest<NeutralActivityDto[]>("/neutral-activities", {
      query: { includeInactive: params?.includeInactive ? "true" : undefined },
    }),
  createNeutralActivity: (input: CreateNeutralActivityInput) =>
    apiRequest<NeutralActivityDto>("/neutral-activities", { method: "POST", body: input }),
  updateNeutralActivity: (id: string, input: UpdateNeutralActivityInput) =>
    apiRequest<NeutralActivityDto>(`/neutral-activities/${id}`, { method: "PATCH", body: input }),
  exportNeutralActivities: (params: { includeInactive?: boolean }) =>
    apiRequestFile("/neutral-activities/export", params),
  importTemplateNeutralActivities: () => apiRequestFileGet("/neutral-activities/import-template"),
  importNeutralActivities: (file: File) =>
    apiRequestUpload<ImportResult>("/neutral-activities/import", file, "file"),
  units: () => apiRequest<UnitDto[]>("/units"),
  createUnit: (input: CreateUnitInput) => apiRequest<UnitDto>("/units", { method: "POST", body: input }),
  updateUnit: (id: string, input: UpdateUnitInput) =>
    apiRequest<UnitDto>(`/units/${id}`, { method: "PATCH", body: input }),
  // `includeInactive` is meaningless for Units (no `active` field on that
  // entity), but the param is kept for a consistent call signature across
  // all 4 export methods — matches the server route, which validates the
  // shape but never reads the value (see CatalogController.exportUnits).
  exportUnits: (params: { includeInactive?: boolean }) => apiRequestFile("/units/export", params),
  importTemplateUnits: () => apiRequestFileGet("/units/import-template"),
  importUnits: (file: File) => apiRequestUpload<ImportResult>("/units/import", file, "file"),
  departments: () => apiRequest<DepartmentDto[]>("/departments"),
  validCombinations: (params: { productId?: string; operationId?: string; date?: string }) =>
    apiRequest<ValidCombinationDto[]>("/catalog/valid-combinations", { query: params }),
};

export const dailyEntryApi = {
  find: (workerId: string, date: string) =>
    apiRequest<DailyWorkEntryDto | null>("/daily-entries", { query: { workerId, date } }),
  create: (workerId: string, workDate: string) =>
    apiRequest<DailyWorkEntryDto>("/daily-entries", { method: "POST", body: { workerId, workDate } }),
  /** "Potvrdi unos" / "Ponovo otvori" (2026-09-09 brainstorm) — the same
   * toggle now cycles both directions, not just draft -> confirmed. */
  updateStatus: (id: string, status: "confirmed" | "draft") =>
    apiRequest<DailyWorkEntryDto>(`/daily-entries/${id}/status`, {
      method: "PATCH",
      body: { status },
    }),
  addLine: (dailyEntryId: string, input: CreateWorkLineInput) =>
    apiRequest<WorkLineDto>(`/daily-entries/${dailyEntryId}/lines`, { method: "POST", body: input }),
  /** Read-only "Istorija izmena" — ADMIN + DIRECTOR, no write counterpart. */
  history: (dailyEntryId: string) =>
    apiRequest<DailyEntryHistoryItemDto[]>(`/daily-entries/${dailyEntryId}/history`),
  /** "Obriši ceo dan" (2026-09-09 brainstorm) — reason is mandatory, enforced server-side. */
  deleteEntry: (id: string, reason: string) =>
    apiRequest<{ deleted: boolean }>(`/daily-entries/${id}`, { method: "DELETE", query: { reason } }),
  /** `null` when nothing was ever deleted for that worker+date — a normal outcome, not an error. */
  deletedInfo: (workerId: string, date: string) =>
    apiRequest<DeletedDailyEntryInfoDto | null>("/daily-entries/deleted-info", { query: { workerId, date } }),
};

export const calculationPolicyApi = {
  /** `null` when no active Calculation Policy Version exists for this tenant — a safe read, never a thrown error. */
  current: () => apiRequest<CalculationPolicyVersionDto | null>("/calculation-policy/current"),
  versions: () => apiRequest<CalculationPolicyVersionDto[]>("/calculation-policy/versions"),
  createVersion: (input: CreateCalculationPolicyVersionInput) =>
    apiRequest<CalculationPolicyVersionDto>("/calculation-policy/versions", { method: "POST", body: input }),
};

export const companyPerformanceSettingsApi = {
  /** Active version on `date` (defaults server-side to today when omitted). */
  current: (date?: string) =>
    apiRequest<CompanyPerformanceSettingsVersionDto | null>("/company-performance-settings/current", {
      query: { date },
    }),
  versions: () =>
    apiRequest<CompanyPerformanceSettingsVersionDto[]>("/company-performance-settings/versions"),
  createVersion: (input: CompanyPerformanceSettingsInput) =>
    apiRequest<CompanyPerformanceSettingsVersionDto>("/company-performance-settings/versions", {
      method: "POST",
      body: input,
    }),
};

export const normsApi = {
  list: (params?: { productId?: string; operationId?: string; includeArchived?: boolean }) =>
    apiRequest<NormDto[]>("/norms", {
      query: { productId: params?.productId, operationId: params?.operationId, includeArchived: params?.includeArchived ? "true" : undefined },
    }),
  create: (input: CreateNormInput) => apiRequest<NormDto>("/norms", { method: "POST", body: input }),
  supersede: (id: string, input: SupersedeNormInput) =>
    apiRequest<NormDto>(`/norms/${id}/supersede`, { method: "POST", body: input }),
  /** Reversible, no reason (2026-09-09) — see NormsService.archive. */
  archive: (id: string) => apiRequest<NormDto>(`/norms/${id}/archive`, { method: "POST" }),
  unarchive: (id: string) => apiRequest<NormDto>(`/norms/${id}/unarchive`, { method: "POST" }),
  /** Only succeeds when the norm's full lineage was never referenced by a WorkLine — see NormsService.remove. */
  remove: (id: string, reason: string) =>
    apiRequest<{ deleted: boolean }>(`/norms/${id}`, { method: "DELETE", query: { reason } }),
  history: (id: string) => apiRequest<NormHistoryItemDto[]>(`/norms/${id}/history`),
};

export const normRecommendationsApi = {
  list: () => apiRequest<NormRecommendationDto[]>("/norm-recommendations"),
};

export const workLinesApi = {
  update: (id: string, input: UpdateWorkLineInput) =>
    apiRequest<WorkLineDto>(`/work-lines/${id}`, { method: "PATCH", body: input }),
  remove: (id: string, reason?: string) =>
    apiRequest<{ deleted: boolean }>(`/work-lines/${id}`, { method: "DELETE", query: { reason } }),
};

/**
 * Analytics backend Slice A (docs/NORMAX_PERFORMANCE_ANALYTICS_BACKEND_SLICE_A_IMPLEMENTATION_REPORT.md).
 * Every call requires an explicit `from`/`to` — this client never chooses a
 * default period itself, matching the backend's own "never silently choose
 * dates" contract; the default/quick-pick period logic lives in
 * `features/analytics/period.ts` (frontend-only, Slice B).
 */
export const analyticsApi = {
  /**
   * The shared Reporting Day resolver (docs/
   * NORMAX_PERFORMANCE_REPORTING_DAY_DASHBOARD_IMPLEMENTATION_REPORT.md) —
   * "the latest calendar day with at least one persisted WorkLine for this
   * tenant," resolved server-side against the tenant's own timezone. No
   * `from`/`to` — this resolves exactly one date, never a period. Dashboard
   * is the only caller today.
   */
  latestReportingDay: () => apiRequest<LatestReportingDayDto>("/analytics/latest-reporting-day"),
  summary: (from: string, to: string) =>
    apiRequest<AnalyticsSummaryDto>("/analytics/summary", { query: { from, to } }),
  workers: (from: string, to: string) =>
    apiRequest<AnalyticsWorkersResponseDto>("/analytics/workers", { query: { from, to } }),
  products: (from: string, to: string) =>
    apiRequest<AnalyticsProductsResponseDto>("/analytics/products", { query: { from, to } }),
  operations: (from: string, to: string) =>
    apiRequest<AnalyticsOperationsResponseDto>("/analytics/operations", { query: { from, to } }),
  activityBreakdown: (from: string, to: string) =>
    apiRequest<AnalyticsActivityBreakdownDto>("/analytics/activity-breakdown", { query: { from, to } }),
};

export const reportsApi = {
  types: () => apiRequest<ReportTypeCatalogEntry[]>("/reports/types"),
  preview: (input: ReportPreviewRequestInput) => apiRequest<ReportDocument>("/reports/preview", { method: "POST", body: input }),
  /** On-demand premium PDF export — same request shape as `preview`, but the
   * response is `application/pdf`. Returns the blob plus the server's
   * `Content-Disposition` filename. */
  downloadPdf: (input: ReportPreviewRequestInput) => apiRequestFile("/reports/pdf", input),
};
